import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import range from 'lodash/range';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { neutral, petrol } from '../../../styles/mui/colors';
import useScreenSize from '../../../util/hooks/useScreenSize';
import { stringify } from '../../../util/urlHelpers';
import IconCircleArrowLeft from '../../Icons/IconCircleArrowLeft';
import IconCircleArrowRight from '../../Icons/IconCircleArrowRight';
import NamedLink from '../NamedLink/NamedLink';

let pgKey = 0;
const paginationGapKey = () => {
  pgKey += 1;
  return pgKey;
};

/**
 * Returns an array containing page numbers and possible ellipsis '…' characters.
 *
 * @param {Number} page - current page
 * @param {Number} totalPages - total page count
 * @returns {Array}
 */
const getPageNumbersArray = (page, totalPages) => {
  // Create array of numbers: [1, 2, 3, 4, ..., totalPages]
  const numbersFrom1ToTotalPages = range(1, totalPages + 1);
  return numbersFrom1ToTotalPages
    .filter((v) => {
      // Filter numbers that are next to current page and pick also first and last page
      // E.g. [1, 4, 5, 6, 9], where current page = 5 and totalPages = 9.
      return v === 1 || Math.abs(v - page) <= 1 || v === totalPages;
    })
    .reduce((newArray, p) => {
      // Create a new array where gaps between consecutive numbers is filled with ellipsis character
      // E.g. [1, '…', 4, 5, 6, '…', 9], where current page = 5 and totalPages = 9.
      const isFirstPageOrNextToCurrentPage = p === 1 || newArray[newArray.length - 1] + 1 === p;
      return isFirstPageOrNextToCurrentPage ? newArray.concat([p]) : newArray.concat(['\u2026', p]);
    }, []);
};

const paginationStyle = {
  border: `1px solid ${neutral[200]}`,
  borderRadius: 1,
  bgcolor: 'white',
  padding: '8px',
};

// Styles for selected and unselected page numbers
const selectedPageStyle = {
  color: petrol.main,
  fontWeight: 'bold',
  fontSize: '16px',
};

const unselectedPageStyle = {
  color: neutral[500],
  fontSize: '16px',
};

/**
 * Component that renders pagination links with previous, next, and page numbers.
 */
const PaginationLinksComponent = ({
  pageName,
  pagePathParams = {},
  pageSearchParams = {},
  pagination,
}) => {
  const intl = useIntl();
  const { page, totalPages } = pagination;
  const prevPage = page > 1 ? page - 1 : null;
  const nextPage = page < totalPages ? page + 1 : null;
  const prevSearchParams = { ...pageSearchParams, page: prevPage };
  const nextSearchParams = { ...pageSearchParams, page: nextPage };
  const { isDesktop } = useScreenSize();

  /* Arrow links: to previous page */
  const prevLinkEnabled = (
    <NamedLink
      name={pageName}
      params={pagePathParams}
      title={intl.formatMessage({ id: 'PaginationLinks.previous' })}
      to={{ search: stringify(prevSearchParams) }}
    >
      <Box alignItems="center" display="flex">
        <IconCircleArrowLeft fontSize="large" sx={{ color: petrol.main }} />
        {isDesktop ? (
          <Typography sx={{ ml: 1, color: petrol.main }} variant="body1">
            <FormattedMessage id="Pagination.prev" />
          </Typography>
        ) : null}
      </Box>
    </NamedLink>
  );

  const prevLinkDisabled = <IconCircleArrowLeft fontSize="large" sx={{ color: neutral[200] }} />;

  /* Arrow links: to next page */
  const nextLinkEnabled = (
    <NamedLink
      name={pageName}
      params={pagePathParams}
      title={intl.formatMessage({ id: 'PaginationLinks.next' })}
      to={{ search: stringify(nextSearchParams) }}
    >
      <Box alignItems="center" display="flex">
        {isDesktop ? (
          <Typography sx={{ mr: 1, color: petrol.main }} variant="body1">
            <FormattedMessage id="Pagination.next" />
          </Typography>
        ) : null}
        <IconCircleArrowRight fontSize="large" sx={{ color: petrol.main }} />
      </Box>
    </NamedLink>
  );

  const nextLinkDisabled = <IconCircleArrowRight fontSize="large" sx={{ color: neutral[200] }} />;

  /* Numbered pagination links */
  const pageNumbersNavLinks = getPageNumbersArray(page, totalPages).map((v) => {
    const isCurrentPage = v === page;

    // Apply the styles based on whether it's the current page
    const pageSx = isCurrentPage ? selectedPageStyle : unselectedPageStyle;

    return typeof v === 'number' ? (
      <NamedLink
        key={v}
        name={pageName}
        params={pagePathParams}
        sx={{ textDecoration: 'none' }}
        title={intl.formatMessage({ id: 'PaginationLinks.toPage' }, { page: v })}
        to={{ search: stringify({ ...pageSearchParams, page: v }) }}
      >
        <Typography sx={{ padding: '8px', ...pageSx }}>{v}</Typography>
      </NamedLink>
    ) : (
      <Box
        key={`pagination_gap_${paginationGapKey()}`}
        sx={{
          padding: '8px',
          lineHeight: '13px',
        }}
      >
        <Typography sx={unselectedPageStyle}>{v}</Typography>
      </Box>
    );
  });

  // Calculate flex-basis based on the number of page links
  const flexBasisValues = {
    1: 30,
    2: 60,
    3: 100,
    4: 160,
    5: 200,
    6: 250,
    7: 275,
  };
  const flexBasis = flexBasisValues[pageNumbersNavLinks.length] || 275;

  return (
    <Box sx={paginationStyle}>
      <Box alignItems="center" display="flex" justifyContent="space-between">
        {prevPage ? prevLinkEnabled : prevLinkDisabled}
        <Box display="flex" justifyContent="space-between" sx={{ flexBasis: `${flexBasis}px` }}>
          {pageNumbersNavLinks}
        </Box>
        {nextPage ? nextLinkEnabled : nextLinkDisabled}
      </Box>
    </Box>
  );
};

PaginationLinksComponent.displayName = 'PaginationLinks';
const PaginationLinks = memo(PaginationLinksComponent);

export default PaginationLinks;
